import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, ArrowRight, ShieldCheck, Loader2, ArrowLeft, RefreshCw } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function CutoverBanner() {
  const [job, setJob] = useState(null);
  useEffect(() => {
    let t;
    const load = () => api.get("/odoo/cutover/status").then((r) => {
      setJob(r.data.data);
      if (r.data.data.status === "running") t = setTimeout(load, 3000);
    }).catch(() => {});
    load();
    return () => clearTimeout(t);
  }, []);
  if (!job || job.status !== "running") return null;
  return (
    <div className="rj-login-dev" data-testid="login-cutover-banner">
      <RefreshCw size={13} className="spin" /> Pemindahan ke Odoo Production sedang berjalan: {job.step} ({job.done}/{job.total}).
      Setelah selesai, login dengan <b>{job.prod_admin_email}</b>.
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const [step, setStep] = useState(1);
  const [identity, setIdentity] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const requestOtp = async (e) => {
    e?.preventDefault();
    if (!identity.trim()) { toast.error("Masukkan email kerja Anda"); return; }
    setBusy(true);
    try {
      const res = await api.post("/auth/request-otp", { identity: identity.trim() });
      setStep(2);
      toast.success(res.data.data.message || "OTP dikirim");
    } catch (err) {
      toast.error(apiError(err, "Gagal mengirim OTP"));
    } finally { setBusy(false); }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();
    if (code.length !== 6) { toast.error("Kode OTP harus 6 digit"); return; }
    setBusy(true);
    try {
      const res = await api.post("/auth/verify-otp", { identity: identity.trim(), code });
      login(res.data.data.token, res.data.data.user);
      toast.success(`Selamat datang, ${res.data.data.user.name}`);
    } catch (err) {
      toast.error(apiError(err, "Verifikasi gagal"));
    } finally { setBusy(false); }
  };

  return (
    <div className="rj-login">
      <div className="rj-login-brand">
        <div className="rj-login-brand-inner">
          <div className="brand-logo-frame" style={{ width: 92, height: 92, flex: "0 0 92px" }}>
            <img src="/rasa-juara-logo-v3.webp?v=3" alt="Rasa Juara Indonesia" className="brand-logo-image" />
          </div>
          <h1>Sales Hub</h1>
          <p className="rj-login-tag">RASA JUARA INDONESIA</p>
          <p className="rj-login-desc">
            Pendamping Odoo untuk tim sales, admin & finance. Kelola Sales Order, customer,
            kunjungan, penagihan, dan sinkronisasi Odoo dalam satu tempat.
          </p>
          <div className="rj-login-badge"><ShieldCheck size={16} /> Login aman tanpa password (OTP)</div>
        </div>
      </div>

      <div className="rj-login-form-wrap">
        <div className="rj-login-card">
          <CutoverBanner />
          {step === 1 ? (
            <form onSubmit={requestOtp} data-testid="login-identity-form">
              <span className="rj-login-eyebrow">MASUK</span>
              <h2>Selamat datang kembali</h2>
              <p className="rj-login-sub">Masukkan email kerja Anda untuk menerima kode OTP.</p>
              <label className="rj-login-label">Email Kerja</label>
              <div className="rj-login-input">
                <Mail size={18} />
                <input data-testid="login-identity-input" autoFocus value={identity}
                  onChange={(e) => setIdentity(e.target.value)} placeholder="nama@rasajuara.id" />
              </div>
              <button className="rj-login-btn" type="submit" disabled={busy} data-testid="login-request-otp-btn">
                {busy ? <Loader2 size={18} className="spin" /> : <>Kirim OTP <ArrowRight size={18} /></>}
              </button>
              <p className="rj-login-hint">Kode OTP dikirim ke email kerja Anda. Berlaku 5 menit.</p>
            </form>
          ) : (
            <form onSubmit={verifyOtp} data-testid="login-otp-form">
              <button type="button" className="rj-login-back" onClick={() => { setStep(1); setCode(""); }}>
                <ArrowLeft size={15} /> Ganti email
              </button>
              <span className="rj-login-eyebrow">VERIFIKASI</span>
              <h2>Masukkan kode OTP</h2>
              <p className="rj-login-sub">Jika email terdaftar, kode 6 digit telah dikirim ke <strong>{identity}</strong>. Cek juga folder spam.</p>
              <label className="rj-login-label">Kode OTP</label>
              <div className="rj-login-input">
                <Mail size={18} />
                <input data-testid="login-otp-input" autoFocus inputMode="numeric" maxLength={6} value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="••••••" style={{ letterSpacing: "0.4em", fontWeight: 800 }} />
              </div>
              <button className="rj-login-btn" type="submit" disabled={busy} data-testid="login-verify-otp-btn">
                {busy ? <Loader2 size={18} className="spin" /> : <>Masuk <ArrowRight size={18} /></>}
              </button>
              <button type="button" className="rj-login-resend" onClick={requestOtp} disabled={busy} data-testid="login-resend-otp-btn">
                Kirim ulang OTP
              </button>
            </form>
          )}
        </div>
        <p className="rj-login-foot">© {new Date().getFullYear()} Rasa Juara Indonesia · Sales Hub</p>
      </div>
    </div>
  );
}
